import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, ArrowRight, Star, ChevronRight } from 'lucide-react';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';
import { useLanguage } from '../context/LanguageContext';
import { CATEGORY_BY_ID } from '../config/blogCategories';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Per-category landing page: /blog/categorie/:slug
 *
 * Acts as an SEO landing page (own title/description, breadcrumbs, intro text)
 * AND a hub of all posts within that category. Featured posts surface to the
 * top so the editor can curate without writing custom code.
 *
 * If the slug doesn't match a known category we redirect to /blog rather than
 * 404, keeping people inside the funnel.
 */
const BlogCategory = () => {
  const { slug } = useParams();
  const { t } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = CATEGORY_BY_ID[slug];

  useEffect(() => {
    if (!category) { setLoading(false); return; }
    fetch(`${API}/api/blog?category=${encodeURIComponent(slug)}&limit=200`)
      .then(r => r.json())
      .then(data => { setPosts(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug, category]);

  if (!category) return <Navigate to="/blog" replace />;

  const formatDate = (iso) => new Date(iso).toLocaleDateString(
    'pl-PL',
    { year: 'numeric', month: 'long', day: 'numeric' },
  );
  const titleOf = (p) => p.title;
  const excerptOf = (p) => p.excerpt;

  const featured = posts.filter(p => p.featured);
  const rest = posts.filter(p => !p.featured);
  const label = category.labelPl;
  const intro = category.introPl;

  return (
    <div style={styles.page}>
      <SEO
        title={`${label} · CasaOthers`}
        description={intro.slice(0, 155)}
      />
      <PageHero
        image="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1920&q=80"
        title={label}
        subtitle={`${posts.length} ${posts.length === 1 ? 'artykuł' : 'artykułów'}`}
        testIdTitle="category-title"
      />

      <div style={styles.container}>
        {/* Breadcrumbs — also feed JSON-LD via SEO component if we ever wire it */}
        <nav style={styles.breadcrumbs} aria-label="Breadcrumb" data-testid="category-breadcrumbs">
          <Link to="/" style={styles.crumbLink}>Start</Link>
          <ChevronRight size={14} color="#8C8B88" />
          <Link to="/blog" style={styles.crumbLink}>Baza wiedzy</Link>
          <ChevronRight size={14} color="#8C8B88" />
          <span style={styles.crumbActive}>{label}</span>
        </nav>

        <p style={styles.intro} data-testid="category-intro">{intro}</p>

        {loading ? (
          <p style={styles.loadingText}>{t('common.loading')}</p>
        ) : posts.length === 0 ? (
          <div style={styles.emptyState} data-testid="category-empty">
            <p style={styles.emptyText}>
              Wkrótce pojawią się tutaj nowe artykuły na ten temat.
            </p>
            <Link to="/blog" style={styles.emptyCta}>
              <ArrowLeft size={14} /> Zobacz wszystkie artykuły
            </Link>
          </div>
        ) : (
          <>
            {featured.length > 0 && (
              <section style={styles.section}>
                <h2 style={styles.sectionTitle}>
                  <Star size={18} color="#C5A059" style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />
                  Wyróżnione
                </h2>
                <div style={styles.grid}>
                  {featured.map(p => <Card key={p.id} post={p} titleOf={titleOf} excerptOf={excerptOf} formatDate={formatDate} t={t} featured />)}
                </div>
              </section>
            )}

            {rest.length > 0 && (
              <section style={styles.section}>
                <h2 style={styles.sectionTitle}>
                  Wszystkie artykuły w tej kategorii
                </h2>
                <div style={styles.grid}>
                  {rest.map(p => <Card key={p.id} post={p} titleOf={titleOf} excerptOf={excerptOf} formatDate={formatDate} t={t} />)}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const Card = ({ post, titleOf, excerptOf, formatDate, t, featured }) => (
  <article style={featured ? styles.cardFeatured : styles.card} data-testid="category-post-card">
    <Link to={`/blog/${post.slug}`} style={styles.cardLink}>
      <div style={styles.imageContainer}>
        <img src={post.cover_image} alt={titleOf(post)} style={styles.image} loading="lazy" />
        {featured && (
          <span style={styles.ribbon}>
            <Star size={11} /> Wyróżnione
          </span>
        )}
      </div>
      <div style={styles.content}>
        <div style={styles.meta}>
          <Calendar size={14} color="#8C8B88" />
          <span style={styles.date}>{formatDate(post.published_at)}</span>
          {post.reading_time > 0 && (
            <>
              <span style={styles.metaDot}>·</span>
              <Clock size={14} color="#8C8B88" />
              <span style={styles.date}>{post.reading_time} min</span>
            </>
          )}
        </div>
        <h3 style={styles.title}>{titleOf(post)}</h3>
        <p style={styles.excerpt}>{excerptOf(post)}</p>
        <span style={styles.readMore}>{t('blog.readMore')} <ArrowRight size={14} /></span>
      </div>
    </Link>
  </article>
);

const styles = {
  page: { minHeight: '100vh', paddingBottom: '4rem', backgroundColor: '#FAF9F6' },
  container: { maxWidth: '1280px', margin: '0 auto', padding: '0 1.5rem' },
  breadcrumbs: {
    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.4rem',
    padding: '1.5rem 0', fontSize: '0.85rem', fontFamily: "'Manrope', sans-serif",
  },
  crumbLink: { color: '#C5A059', textDecoration: 'none', fontWeight: 500 },
  crumbActive: { color: '#1A1918', fontWeight: 600 },
  intro: {
    color: '#585755', fontSize: '1.05rem', lineHeight: 1.7,
    maxWidth: '760px', marginBottom: '2.5rem',
  },
  section: { marginBottom: '3.5rem' },
  sectionTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.5rem', color: '#1A1918',
    marginBottom: '1.5rem', fontWeight: 500,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '1.75rem',
  },
  card: {
    backgroundColor: 'white', border: '1px solid #EAE5DD', overflow: 'hidden',
    transition: 'all 0.25s ease',
  },
  cardFeatured: {
    backgroundColor: 'white', border: '1px solid #C5A059', overflow: 'hidden',
    boxShadow: '0 6px 20px rgba(197, 160, 89, 0.12)',
    transition: 'all 0.25s ease',
  },
  cardLink: { textDecoration: 'none', color: 'inherit', display: 'block' },
  imageContainer: { width: '100%', height: '220px', overflow: 'hidden', position: 'relative' },
  image: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  ribbon: {
    position: 'absolute', top: '0.85rem', left: '0.85rem',
    display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
    backgroundColor: '#C5A059', color: 'white',
    padding: '0.3rem 0.6rem', fontSize: '0.7rem', fontWeight: 700,
    letterSpacing: '0.04em', textTransform: 'uppercase',
  },
  content: { padding: '1.5rem' },
  meta: { display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.75rem', flexWrap: 'wrap' },
  metaDot: { color: '#C5A059', margin: '0 0.1rem' },
  date: { color: '#8C8B88', fontSize: '0.8rem' },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.25rem', color: '#1A1918',
    marginBottom: '0.85rem', lineHeight: 1.3, fontWeight: 500,
  },
  excerpt: { color: '#585755', fontSize: '0.92rem', lineHeight: 1.65, marginBottom: '1.25rem' },
  readMore: {
    display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
    color: '#C5A059', fontSize: '0.88rem', fontWeight: 600,
  },
  loadingText: { textAlign: 'center', color: '#8C8B88', padding: '3rem 0' },
  emptyState: { textAlign: 'center', padding: '3rem 1rem', backgroundColor: 'white', border: '1px solid #EAE5DD' },
  emptyText: { color: '#585755', fontSize: '1rem', marginBottom: '1rem' },
  emptyCta: {
    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
    backgroundColor: '#1A1918', color: '#C5A059', border: 'none',
    padding: '0.7rem 1.5rem', fontSize: '0.82rem', fontWeight: 700,
    letterSpacing: '0.06em', textTransform: 'uppercase',
    textDecoration: 'none', fontFamily: "'Manrope', sans-serif",
  },
};

export default BlogCategory;
